import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../../config/axios.config';
import { queryKeys } from '../../lib/queryClient';

/**
 * One definition of the query, shared by the hook and by imperative refreshes,
 * so a caller that needs a guaranteed-fresh read still writes into the same
 * cache entry every other consumer is reading.
 */
export const reportServiceQuery = (serviceId) => ({
    queryKey: queryKeys.reportService(serviceId),
    queryFn: async ({ signal }) => {
        // `signal` wires React Query's cancellation to axios, so navigating
        // away mid-flight aborts the request instead of resolving into a
        // component that has unmounted (#194 counted 152 uncancelled fetches).
        const res = await axiosInstance.get(`/user/report-services/${serviceId}`, { signal });
        if (!res.data?.success) {
            throw new Error(res.data?.message || 'Failed to load service details');
        }
        return res.data.data;
    },
});

/**
 * Credits + metadata for one API service (#161, and the concrete case in #202).
 *
 * THE PATTERN THIS REPLACES
 * -------------------------
 * 65 components each ran their own `useEffect` calling
 * `/user/report-services/{id}` on mount, with caching deliberately disabled —
 * CLAUDE.md even documented "Always Fresh Data: fetches on every component
 * mount" and "No Caching" as the house style. On a page that renders several
 * report cards that is several identical requests in flight at once, repeated
 * on every navigation back to the page.
 *
 * WHY CACHING IS SAFE HERE, WHEN THE OLD COMMENT SAID IT WASN'T
 * -------------------------------------------------------------
 * The original reasoning was sound as far as it went: `credits_required` and
 * `available_credits` change after a report runs, so a stale value misleads the
 * user about what they are about to spend. Refetch-on-every-mount was a blunt
 * way to stay correct.
 *
 * The precise fix is invalidation, not refetching: the balance only changes when
 * THIS app spends credits, and we know exactly when that happens. So the cache
 * is dropped at that moment via `useInvalidateReportService`, and served from
 * cache otherwise. That is strictly more correct than the old behaviour — a
 * mount-time refetch raced the deduction and could show a pre-charge balance
 * anyway, whereas invalidation is ordered after it.
 *
 * `staleTime` is intentionally short. Credits are money on screen; 15s bounds
 * how wrong the number can be if some other tab or teammate spends them, while
 * still collapsing the burst of identical mounts this hook exists to fix.
 */
export const useReportService = (serviceId, options = {}) => {
    const query = useQuery({
        ...reportServiceQuery(serviceId),
        // A missing serviceId is a legitimate state (the form is embedded and the
        // host has not resolved one yet), not an error to retry.
        enabled: serviceId !== undefined && serviceId !== null && serviceId !== '',
        staleTime: 15_000,
        ...options,
    });

    return query;
};

/**
 * Force a fresh read and return it.
 *
 * For the one caller that genuinely cannot use a cached value: the verification
 * sidebar measures "credits deducted" as a BEFORE/AFTER difference, so the after
 * read must hit the server. Going through fetchQuery (rather than a bare axios
 * call) means that fresh value also lands in the cache, updating every other
 * mounted consumer instead of leaving them stale.
 */
export const useRefreshReportService = () => {
    const queryClient = useQueryClient();

    // Stable identity so callers can safely list it in effect dependencies.
    return useCallback(
        (serviceId) =>
            queryClient.fetchQuery({
                ...reportServiceQuery(serviceId),
                staleTime: 0,
            }),
        [queryClient]
    );
};

/**
 * Drop the cached credits for a service after something spent them.
 *
 * Call this after any successful report fetch. It is the counterpart that makes
 * caching safe — see the note above.
 */
export const useInvalidateReportService = () => {
    const queryClient = useQueryClient();

    // Stable identity: callers put this in useEffect dependency arrays, and a
    // fresh function each render would re-run those effects on every render.
    return useCallback(
        (serviceId) =>
            queryClient.invalidateQueries({
                // Explicit null/undefined check, not truthiness (#446). Service
                // ids are numeric, and `0 ? … : …` sent a real id down the
                // invalidate-everything branch — the one case where being
                // wrong is silent, because invalidating more than you meant to
                // still looks like it worked.
                queryKey: serviceId === undefined || serviceId === null || serviceId === ''
                    ? queryKeys.reportServices()
                    : queryKeys.reportService(serviceId),
            }),
        [queryClient]
    );
};

export default useReportService;
